import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import UpdateBanner from './UpdateBanner';
import toast from '../../utils/toast';

export default function PWAUpdateManager() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const updateSWRef = useRef<null | ((reloadPage?: boolean) => void | Promise<void>)>(null);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const updateSW = registerSW({
        immediate: true,
        onNeedRefresh() {
          setNeedRefresh(true);
          // Evitar aplicar atualização no meio do fluxo OAuth (callback)
          // pois pode interromper o processamento do hash (#access_token) / ?code
          try {
            const isAuthCallback = window.location.pathname.startsWith('/auth');
            const hasAuthHash = window.location.hash.includes('access_token=');
            const hasAuthCode = new URLSearchParams(window.location.search).has('code');
            if (isAuthCallback && (hasAuthHash || hasAuthCode)) {
              // Deferir update: o banner aparecerá; o reload ocorrerá quando o usuário sair do callback
              return;
            }
            // Aplicar atualização automaticamente fora do callback
            window.setTimeout(() => {
              try { updateSWRef.current?.(true); } catch { /* ignore */ }
            }, 300);
          } catch { /* ignore */ }
        },
        onOfflineReady() {
          toast.success('Aplicativo pronto para uso offline');
        },
        // Força checagem de atualização após registrar o SW e periodicamente
        onRegisteredSW(_swUrl, registration) {
          try {
            // Checagem inicial após 2s (captura SW novo logo após deploy)
            window.setTimeout(() => registration?.update().catch(() => {}), 2000);
            // Checagem a cada 30min enquanto o app estiver aberto
            window.setInterval(() => registration?.update().catch(() => {}), 30 * 60 * 1000);
          } catch { /* ignore */ }
        },
      });
      updateSWRef.current = updateSW;

      // Revalidar quando o app voltar ao foco/visível (com ou sem PWA)
      const onVisibility = () => {
        if (document.visibilityState === 'visible') {
          try { updateSWRef.current?.(); } catch { /* ignore */ }
        }
      };
      const onFocus = () => {
        try { updateSWRef.current?.(); } catch { /* ignore */ }
      };
      window.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onFocus);
      return () => {
        window.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('focus', onFocus);
      };
    }
  }, []);

  const handleUpdate = () => {
    try {
      const fn = updateSWRef.current;
      if (fn) {
        const res = fn(true);
        if (res && typeof (res as Promise<void>).then === 'function') {
          (res as Promise<void>).catch(() => window.location.reload());
        }
      } else {
        window.location.reload();
      }
    } finally {
      setNeedRefresh(false);
    }
  };

  const handleDismiss = () => setNeedRefresh(false);

  return needRefresh ? (
    <UpdateBanner onUpdate={handleUpdate} onDismiss={handleDismiss} />
  ) : null;
}
